import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import API_BASE_URL from '../config/api';

// Grounded chat over the wiki + its source documents, with [n] citations.
// Ported from the labs_system "Fråga AI" (BidBot) panel.
//
// Attached documents never become part of the wiki: the browser holds the file
// and sends it only when needed, and generated documents are downloaded straight
// to the user's computer. Small files stay entirely in the request as base64;
// files too large for that are staged in a per-user, daily-expiring S3 prefix.
//
// A bare [3] is not markdown link syntax, so it survives parsing as a literal
// text node. This rehype plugin re-wraps those runs in a <cite> element, which
// we then render as a button that opens the cited source.
const CITE_MARKER = /\[\s*\d+(?:\s*[,–-]\s*\d+)*\s*\]/g;

// Lambda rejects a request payload over 6 MB before the function is invoked, and
// base64 adds ~33% — so only small files are inlined. Anything larger is PUT
// straight to S3 (wiki/tmp/, expired daily) and passed to the API by s3Key.
const MAX_INLINE_BYTES = 3 * 1024 * 1024;
const MAX_ATTACH_BYTES = 40 * 1024 * 1024;
const MAX_ATTACH_MB = MAX_ATTACH_BYTES / (1024 * 1024);

function rehypeCitations() {
  const walk = (node) => {
    if (!Array.isArray(node.children)) return;
    const out = [];
    for (const child of node.children) {
      if (child.type === 'text' && CITE_MARKER.test(child.value)) {
        CITE_MARKER.lastIndex = 0;
        const val = child.value;
        let last = 0;
        let m;
        while ((m = CITE_MARKER.exec(val)) !== null) {
          if (m.index > last) out.push({ type: 'text', value: val.slice(last, m.index) });
          out.push({
            type: 'element', tagName: 'cite', properties: {},
            children: [{ type: 'text', value: m[0] }],
          });
          last = m.index + m[0].length;
        }
        if (last < val.length) out.push({ type: 'text', value: val.slice(last) });
      } else {
        walk(child);
        out.push(child);
      }
    }
    node.children = out;
  };
  return (tree) => walk(tree);
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1]);
  r.onerror = reject;
  r.readAsDataURL(file);
});

const triggerDownload = (url, filename, revoke) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking the object URL synchronously after click can cancel the download
  // and make the browser open the blob instead, so defer it.
  if (revoke) setTimeout(() => URL.revokeObjectURL(url), 4000);
};

const downloadBase64 = (b64, filename) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  // Force a download for PDFs too (which a browser would otherwise open inline)
  // by using an octet-stream blob + the download attribute.
  triggerDownload(URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' })),
    filename, true);
};

// A produced document comes back either inline as base64 or, when too large for
// the Lambda response cap, as a presigned S3 URL. The URL is already signed with
// Content-Disposition: attachment, so a plain anchor click downloads it.
const downloadResult = (data) => {
  if (data.downloadUrl) triggerDownload(data.downloadUrl, data.filename, false);
  else downloadBase64(data.contentBase64, data.filename);
};

const COPY = {
  sv: {
    placeholder: 'Fråga kunskapsbasen…',
    send: 'Fråga',
    thinking: 'Söker i källorna…',
    sources: 'Källor',
    empty: 'Ställ en fråga om forskningen i kunskapsbasen. Svaren är grundade i källorna och citerar dem. Du kan även bifoga ett Word- eller PDF-dokument och be om ändringar.',
    error: 'Något gick fel. Försök igen.',
    clear: 'Rensa',
    newQuestion: 'Ställ ny fråga',
    history: 'Tidigare dialoger',
    noHistory: 'Inga sparade dialoger än.',
    del: 'Ta bort',
    attach: 'Bifoga dokument',
    attached: 'Bifogat',
    remove: 'Ta bort',
    reading: 'Läser dokumentet…',
    tooBig: `Dokumentet är för stort (max ${MAX_ATTACH_MB} MB).`,
    badType: 'Endast .docx och .pdf stöds.',
    saveWord: 'Spara som Word',
    savePdf: 'Spara som PDF',
    savingPdf: 'Skapar PDF…',
    proposal: 'Föreslagna ändringar',
    apply: 'Tillämpa och ladda ner',
    applying: 'Tillämpar (PDF kan ta ~15 s)…',
    applied: 'Ändringar tillämpade — dokumentet har laddats ner.',
    missed: 'Kunde inte hittas i dokumentet',
    onlyDocx: 'Ändringar kan bara tillämpas på .docx- eller .pdf-filer.',
    note: 'Bifogade dokument sparas inte i wikin — de används bara i det här samtalet.',
    truncated: 'Dokumentet är långt — bara början av texten används i svaren.',
    suggestions: [
      'Vad säger forskningen om grönska och stress?',
      'Hur påverkar gångvänlighet folkhälsan?',
      'Vad är biofil design?',
    ],
  },
  en: {
    placeholder: 'Ask the knowledge base…',
    send: 'Ask',
    thinking: 'Searching the sources…',
    sources: 'Sources',
    empty: 'Ask a question about the research in the knowledge base. Answers are grounded in the sources and cite them. You can also attach a Word or PDF document and ask for changes.',
    error: 'Something went wrong. Please try again.',
    clear: 'Clear',
    newQuestion: 'Ask new question',
    history: 'Previous dialogs',
    noHistory: 'No saved dialogs yet.',
    del: 'Delete',
    attach: 'Attach document',
    attached: 'Attached',
    remove: 'Remove',
    reading: 'Reading the document…',
    tooBig: `The document is too large (max ${MAX_ATTACH_MB} MB).`,
    badType: 'Only .docx and .pdf are supported.',
    saveWord: 'Save as Word',
    savePdf: 'Save as PDF',
    savingPdf: 'Creating PDF…',
    proposal: 'Proposed changes',
    apply: 'Apply and download',
    applying: 'Applying (PDF may take ~15s)…',
    applied: 'Changes applied — the document has been downloaded.',
    missed: 'Could not be found in the document',
    onlyDocx: 'Changes can only be applied to .docx or .pdf files.',
    note: 'Attached documents are not added to the wiki — they are used only in this conversation.',
    truncated: 'The document is long — only the beginning of the text is used in answers.',
    suggestions: [
      'What does the research say about greenery and stress?',
      'How does walkability affect public health?',
      'What is biophilic design?',
    ],
  },
};

const WikiChat = ({ lang, onOpenDoc, onOpenWikiPage }) => {
  const t = COPY[lang] || COPY.sv;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [attachment, setAttachment] = useState(null); // {filename, text, base64|null, s3Key|null}
  const [reading, setReading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, busy]);

  // Small files are inlined as base64; larger ones are PUT to S3 first and then
  // referenced by s3Key, since an inline payload over ~4.5MB is rejected by
  // Lambda before the function ever runs. Either way the ref (base64 or s3Key)
  // is kept so the edit endpoints can reuse the same bytes.
  const stageFile = async (file) => {
    if (file.size <= MAX_INLINE_BYTES) {
      return { base64: await fileToBase64(file), s3Key: null };
    }
    const res = await fetch(`${API_BASE_URL}/wiki/doc/upload-url`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `upload-url failed (${res.status})`);
    const put = await fetch(data.uploadUrl, { method: 'PUT', body: file });
    if (!put.ok) throw new Error(`upload failed (${put.status})`);
    return { base64: null, s3Key: data.s3Key };
  };

  const handleAttach = async (file) => {
    if (!file) return;
    setError('');
    const name = file.name.toLowerCase();
    if (!name.endsWith('.docx') && !name.endsWith('.pdf')) { setError(t.badType); return; }
    if (file.size > MAX_ATTACH_BYTES) { setError(t.tooBig); return; }

    setReading(true);
    try {
      const { base64, s3Key } = await stageFile(file);
      const res = await fetch(`${API_BASE_URL}/wiki/doc/read`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, contentBase64: base64, s3Key }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `read failed (${res.status})`);
      setAttachment({
        filename: file.name, text: data.text, base64, s3Key,
        truncated: !!data.truncated, chars: data.chars,
      });
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/wiki/conversations`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setConversations(d.conversations || []); }
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchConversations(); }, []);

  const newConversation = () => {
    setMessages([]); setConversationId(null); setAttachment(null);
    setError(''); setShowHistory(false);
  };

  const loadConversation = async (id) => {
    setShowHistory(false);
    try {
      const res = await fetch(`${API_BASE_URL}/wiki/conversations/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('load failed');
      const d = await res.json();
      setMessages((d.messages || []).map((m) => ({
        role: m.role, content: m.content, citations: m.citations || [],
      })));
      setConversationId(id);
      setAttachment(null); setError('');
    } catch (err) { setError(t.error); }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE_URL}/wiki/conversations/${id}`, { method: 'DELETE', credentials: 'include' });
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (id === conversationId) newConversation();
    } catch (err) { console.error(err); }
  };

  const ask = async (question) => {
    const q = (question ?? input).trim();
    if (!q || busy) return;
    setError('');
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE_URL}/wiki/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q,
          conversationId,
          // only the extracted text travels; the file stays in the browser
          attachment: attachment ? { filename: attachment.filename, text: attachment.text } : null,
        }),
      });
      if (!res.ok) throw new Error('chat failed');
      const data = await res.json();
      setConversationId(data.conversationId);
      setMessages((m) => [...m, {
        role: 'assistant',
        content: data.answer,
        citations: data.citations || [],
        proposal: data.proposal || null,
      }]);
      fetchConversations();
    } catch (err) {
      console.error(err);
      setError(t.error);
    } finally {
      setBusy(false);
    }
  };

  const openCitation = (c) => {
    if (!c) return;
    if (c.kind === 'wiki' && c.slug) onOpenWikiPage?.(c.slug);
    else if (c.doc_id) onOpenDoc?.(c.doc_id, c.filename, c.page);
  };

  const applyProposal = async (msgIndex, proposal) => {
    if (!attachment) return;
    const isPdf = attachment.filename.toLowerCase().endsWith('.pdf');
    const isDocx = attachment.filename.toLowerCase().endsWith('.docx');
    if (!isPdf && !isDocx) { setError(t.onlyDocx); return; }
    setApplying(true);
    setError('');
    try {
      // .docx edits in place; .pdf goes via Adobe (PDF -> docx -> edit -> PDF)
      const endpoint = isPdf ? '/wiki/pdf/edit' : '/wiki/doc/edit';
      const res = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: attachment.filename,
          contentBase64: attachment.base64,
          s3Key: attachment.s3Key,
          edits: proposal.edits,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `edit failed (${res.status})`);

      downloadResult(data);
      // For .docx keep working from the edited copy (which, if it was too large
      // to inline, came back as a staged s3Key). For .pdf the returned file is a
      // re-exported PDF, not the same base to re-edit, so leave the attachment
      // as-is (the user re-attaches if they want another round).
      if (isDocx) {
        setAttachment((a) => ({
          ...a, base64: data.contentBase64 || null, s3Key: data.s3Key || null,
        }));
      }
      setMessages((m) => m.map((msg, i) => i === msgIndex
        ? { ...msg, applyResult: { applied: data.applied, missed: data.missed } }
        : msg));
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setApplying(false);
    }
  };

  const [savingPdfIdx, setSavingPdfIdx] = useState(null);
  const saveAs = async (idx, content, citations, format) => {
    if (format === 'pdf') setSavingPdfIdx(idx);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/wiki/doc/create`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // citations -> an APA reference list is appended to the document
        body: JSON.stringify({ title: 'Urban Wiki', markdown: content, citations: citations || [], format }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `create failed (${res.status})`);
      downloadResult(data);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      if (format === 'pdf') setSavingPdfIdx(null);
    }
  };

  return (
    <div className="wc">
      <div className="wc-toolbar">
        <button className="wc-new" onClick={newConversation} data-tip="wc.new">+ {t.newQuestion}</button>
        <div className="wc-history-wrap">
          <button className="wc-history-btn" onClick={() => setShowHistory((v) => !v)} data-tip="wc.history">
            {t.history}{conversations.length ? ` (${conversations.length})` : ''} ▾
          </button>
          {showHistory && (
            <div className="wc-history-menu">
              {conversations.length === 0 ? (
                <p className="wiki-muted wc-history-empty">{t.noHistory}</p>
              ) : conversations.map((c) => (
                <div key={c.id}
                     className={`wc-history-item ${c.id === conversationId ? 'active' : ''}`}
                     onClick={() => loadConversation(c.id)} data-tip="wc.historyItem">
                  <span className="wc-history-title">{c.title}</span>
                  <button className="wc-history-del" aria-label={t.del} data-tip="wc.del"
                          onClick={(e) => deleteConversation(c.id, e)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="wc-log">
        {messages.length === 0 && !busy && (
          <div className="wc-empty">
            <p className="wiki-muted">{t.empty}</p>
            <div className="wc-suggestions">
              {t.suggestions.map((s) => (
                <button key={s} className="wc-suggestion" onClick={() => ask(s)} data-tip="wc.suggestion">{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`wc-msg wc-${m.role}`}>
            {m.role === 'assistant' ? (
              <>
                <div className="wiki-md">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeCitations]}
                    components={{
                      cite: ({ children }) => {
                        const txt = Array.isArray(children) ? children.join('') : String(children ?? '');
                        const nums = (txt.match(/\d+/g) || []).map(Number);
                        const target = (m.citations || []).find((c) => nums.includes(c.n));
                        if (!target) return <span className="wc-cite-flat">{children}</span>;
                        const where = target.kind === 'wiki'
                          ? target.filename || target.slug
                          : `${target.filename}${target.page ? `, s. ${target.page}` : ''}`;
                        return (
                          <button type="button" className="wc-cite"
                                  data-tip={`${lang === 'en' ? 'Open' : 'Öppna'} ${where}`}
                                  onClick={() => openCitation(target)}>
                            {children}
                          </button>
                        );
                      },
                    }}
                  >
                    {m.content}
                  </ReactMarkdown>
                </div>

                {/* proposed document edits, reviewed before anything is applied */}
                {m.proposal && (
                  <div className="wc-proposal">
                    <strong>{t.proposal} ({m.proposal.edits.length})</strong>
                    <ul>
                      {m.proposal.edits.map((e, k) => (
                        <li key={k}>
                          <span className="wc-find">{e.find}</span>
                          <span className="wc-arrow">→</span>
                          <span className="wc-replace">{e.replace}</span>
                          {e.reason && <em className="wc-reason">{e.reason}</em>}
                        </li>
                      ))}
                    </ul>
                    {m.applyResult ? (
                      <p className="wc-applied">
                        {t.applied}
                        {m.applyResult.missed?.length > 0 && (
                          <span className="wc-missed">
                            {' '}({t.missed}: {m.applyResult.missed.map((x) => `"${x.find}"`).join(', ')})
                          </span>
                        )}
                      </p>
                    ) : (
                      <button className="wc-apply" disabled={applying || !attachment} data-tip="wc.apply"
                              onClick={() => applyProposal(i, m.proposal)}>
                        {applying ? t.applying : t.apply}
                      </button>
                    )}
                  </div>
                )}

                <div className="wc-actions">
                  <button className="wc-saveword" onClick={() => saveAs(i, m.content, m.citations, 'docx')} data-tip="wc.saveWord">
                    {t.saveWord}
                  </button>
                  <button className="wc-saveword" disabled={savingPdfIdx === i} data-tip="wc.savePdf"
                          onClick={() => saveAs(i, m.content, m.citations, 'pdf')}>
                    {savingPdfIdx === i ? t.savingPdf : t.savePdf}
                  </button>
                </div>

                {m.citations && m.citations.length > 0 && (
                  <details className="wc-sources">
                    <summary>{t.sources} ({m.citations.length})</summary>
                    <ul>
                      {m.citations.map((c) => (
                        <li key={c.n}>
                          <button onClick={() => openCitation(c)} data-tip="wc.source">
                            [{c.n}] {c.kind === 'wiki'
                              ? `${c.filename || c.slug} (wiki)`
                              : `${c.filename}${c.page ? `, s. ${c.page}` : ''}`}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            ) : (
              <p>{m.content}</p>
            )}
          </div>
        ))}

        {busy && <div className="wc-msg wc-assistant"><p className="wiki-muted">{t.thinking}</p></div>}
        {error && <p className="wiki-doc-error">{error}</p>}
        <div ref={endRef} />
      </div>

      {attachment && (
        <div className="wc-attachment">
          <span className="wc-attach-name">📎 {t.attached}: {attachment.filename}</span>
          <button onClick={() => setAttachment(null)} data-tip="wc.remove">{t.remove}</button>
          <span className="wc-attach-note">{t.note}</span>
          {attachment.truncated && (
            <span className="wc-attach-warn">⚠ {t.truncated}</span>
          )}
        </div>
      )}

      <form className="wc-input" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <label className="wc-attach-btn" aria-label={t.attach} data-tip="wc.attach">
          📎
          <input ref={fileRef} type="file" accept=".docx,.pdf" hidden
                 onChange={(e) => handleAttach(e.target.files[0])} />
        </label>
        <input
          type="text"
          value={input}
          placeholder={reading ? t.reading : t.placeholder}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy || reading}
        />
        <button type="submit" disabled={busy || reading || !input.trim()} data-tip="wc.send">{t.send}</button>

      </form>
    </div>
  );
};

export default WikiChat;
